<template>
  <v-dialog v-model="mostrar" max-width="800px" persistent>
    <v-card>
      <v-card-title class="text-h6">
        {{ esEdicion ? 'Editar producto' : 'Crear producto' }} – {{ gridSeleccionado?.nombre }}
      </v-card-title>

      <v-card-text>
        <v-form ref="formProducto" v-model="formValido">
          <v-container fluid>
            <v-row>
              <v-col
                v-for="col in gridSeleccionado?.gridConfig || []"
                :key="col.nombre"
                cols="12"
                md="6"
              >
                <!-- Campos simples -->
                <v-text-field
                  v-if="col.tipo !== 'estatico' && col.tipo !== 'emplazamiento'"
                  v-model="valores[col.nombre]"
                  :label="col.nombre"
                  :rules="[reglaObligatorio]"
                  outlined
                  dense
                />

                <!-- Selector estático -->
                <v-select
                  v-else-if="col.tipo === 'estatico'"
                  v-model="valores[col.nombre]"
                  :items="itemsEstatico(col)"
                  :label="col.nombre"
                  :multiple="col.selector?.multiple || false"
                  :rules="[reglaObligatorio]"
                  outlined
                  dense
                />

                <!-- Selector por dirección del cliente -->
                <v-select
                  v-else-if="col.tipo === 'emplazamiento'"
                  v-model="valores[col.nombre]"
                  :items="direccionesInstalacion"
                  :label="col.nombre"
                  :rules="[reglaObligatorio]"
                  chips
                  outlined
                  dense
                >
                  <!-- Mostrar formato multiline bonito -->
                  <template v-slot:item="{ item }">
                    <div>
                      <strong>{{ item.text.split('\n')[0] }}</strong>
                      <div class="text-caption">
                        {{ item.text.split('\n')[1] }}
                      </div>
                    </div>
                  </template>

                  <template v-slot:selection="{ item }">
                    <v-chip small>
                      {{ item.text.split('\n')[0] }}
                    </v-chip>
                  </template>
                </v-select>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text :disabled="cargando" @click="cerrar">Cancelar</v-btn>
        <v-btn
          color="primary"
          text
          :loading="cargando"
          :disabled="cargando"
          @click="guardarProducto"
        >
          {{ esEdicion ? 'Guardar cambios' : 'Crear' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'CrearProductoDialog',

  props: {
    mostrar: Boolean,
    gridSeleccionado: Object,
    cliente: Object, // viene desde ProductosAuditoria
    auditoriaUuid: {
      type: String,
      required: true,
    },
    // si viene producto => modo edición
    producto: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      valores: {},
      formValido: false,
      cargando: false,
    }
  },

  computed: {
    columnaEmplazamiento() {
      return this.gridSeleccionado?.gridConfig?.find(
        c => c.tipo === 'emplazamiento'
      ) || null
    },
    esEdicion() {
      return !!this.producto
    },

    /** Direcciones tipo instalación y activas */
    direccionesInstalacion() {
      return (this.cliente?.direcciones || [])
        .filter((d) => d.tipo === 'Instalación' && d.isActive)
        .map((d) => ({
          text: `${d.nombre} ${d.codigo ? '-'+d.codigo+'-': ''}(${d.combinada})`,
          value: d.uuid,
        }))
    },
  },

  watch: {
    mostrar(val) {
      if (val && this.gridSeleccionado) {
        this.inicializarValores()
      }
    },
    producto: {
      immediate: true,
      handler(val) {
        if (this.mostrar && val) {
          this.inicializarValores()
        }
      },
    },
    gridSeleccionado: {
      immediate: true,
      handler() {
        this.tryInicializar()
      },
    },
  },

  methods: {
    tryInicializar() {
      if (
        this.mostrar &&
        this.gridSeleccionado &&
        (!this.esEdicion || this.producto)
      ) {
        this.inicializarValores()
      }
    },
    // regla genérica: todos los campos obligatorios
    reglaObligatorio(valor) {
      if (Array.isArray(valor)) {
        return valor.length > 0 || 'Campo obligatorio'
      }
      return (valor !== null && valor !== undefined && String(valor).trim() !== '') ||
        'Campo obligatorio'
    },

    itemsEstatico(col) {
      // Por defecto usamos lo que venga en el grid.
      // Si quieres usar store/llamada extra para refrescar valores,
      // aquí es el sitio para engancharlo.
      return (col.selector?.valor || []).map((v) => v.texto)
    },

    async inicializarValores() {
      this.gridSeleccionado.gridConfig = this.gridSeleccionado.gridConfig
        .filter(col => col.define_alcance !== false)

      for (const col of this.gridSeleccionado.gridConfig) {
        // Si estamos editando, precarga desde producto.params
        if (this.esEdicion && this.producto && Array.isArray(this.producto.params)) {
          const param = this.producto.params.find((p) => p.nombre === col.nombre)
          if (param) {
            if (col.tipo === 'emplazamiento') {
              this.valores[col.nombre] =
                this.producto?.emplazamiento?.direccion?.uuid || ''
              continue
            }
            if (col.selector?.multiple) {
              this.valores[col.nombre] = Array.isArray(param.valor) ? param.valor : []
            } else {
              this.valores[col.nombre] = Array.isArray(param.valor)
                ? param.valor[0] || ''
                : (param.valor || '')
            }
            continue
          }
        }

        // Si no hay producto o no hay param, inicializa vacío
        this.valores[col.nombre] = col.selector?.multiple ? [] : ''
      }

      // reseteamos validación del formulario
      if (this.$refs.formProducto) {
        this.$refs.formProducto.resetValidation()
      }
    },

    cerrar() {
      this.$emit('update:mostrar', false)
    },

    async guardarProducto() {
      // 1) Validar formulario
      const form = this.$refs.formProducto
      if (form && !form.validate()) {
        return
      }

      // 2) Construir params
      const params = []

      for (const col of this.gridSeleccionado.gridConfig) {
        if (col.tipo === 'emplazamiento') continue

        const valor = this.valores[col.nombre]

        params.push({
          nombre: col.nombre,
          valor: Array.isArray(valor) ? valor : [valor],
          multiple: Array.isArray(valor),
          displayValue: Array.isArray(valor) ? valor.join(', ') : valor,
          tipo: col.tipo || 'character',
        })
      }

      // 3) Payload común
      const payload = {
        auditoria: this.auditoriaUuid,
        direccionUuid: this.valores[this.columnaEmplazamiento?.nombre],
        gridUuid: this.gridSeleccionado.uuid,
        params,
        productoReferencia: null,
      }

      if (this.esEdicion && this.producto?.uuid) {
        payload.productoUuid = this.producto.uuid
      }

      this.cargando = true

      try {
        if (this.esEdicion) {
          // MODIFICAR
          await this.$axios.$post('auditorias/modificarProducto', payload)
        } else {
          // CREAR
          await this.$axios.$post('auditorias/crearProducto', payload)
        }

        this.$emit('guardado')
        this.$emit('update:mostrar', false)

      } catch (error) {
        const status = error.response?.status

        this.$store.commit('notification/show', {
          text: status === 422
            ? error.response?.data?.['hydra:description']
            : 'No se pudo guardar el producto',
          color: 'error',
        })
      } finally {
        this.cargando = false
      }
    },
  },
}
</script>
